/**
 * "addressFieldsFromCountry" — country-driven address block for SurveyJS forms.
 *
 * The backend emits a single "addressFieldsFromCountry" marker element in place of the
 * admin's individual address questions. At runtime we EXPAND that marker into real
 * survey questions on the survey's own page/panel:
 *
 *   1. registerAddressFieldsFromCountry()   — registers the type so the JSON parses.
 *   2. expandAddressFieldsFromCountry(survey) — for each marker:
 *        - inserts a real "dropdown" country question at the marker's position,
 *        - removes the marker,
 *        - on country change, adds/removes real "text" questions (from getaddressstructure)
 *          right after the country dropdown, in the same container.
 *
 * Because these are ordinary survey questions, SurveyJS handles theming, blur handling,
 * required validation and value binding natively — no manual syncing needed.
 */
import { Serializer, ElementFactory, Question } from "survey-core";

const CUSTOM_TYPE = "addressFieldsFromCountry";

// Address-structure roles we never render as their own field:
//  - COUNTRY is the dropdown itself
//  - JOBTITLE / DEPARTMENT are not part of the postal address
const FIELDS_TO_SKIP = ["JOBTITLE", "DEPARTMENT", "COUNTRY"];

// ── registration (so the survey JSON parses the "addressFieldsFromCountry" element) ──
let _registered = false;

export function registerAddressFieldsFromCountry() {
    if (_registered) return;
    try {
        class AddressFieldsFromCountryQuestion extends Question {
            getType() {
                return CUSTOM_TYPE;
            }
        }
        if (!Serializer.findClass(CUSTOM_TYPE)) {
            Serializer.addClass(CUSTOM_TYPE, [], () => new AddressFieldsFromCountryQuestion(""), "question");
        }
        ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name) => new AddressFieldsFromCountryQuestion(name));
        _registered = true;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("addressFieldsFromCountry registration warning:", e);
    }
}

// ── helpers ──────────────────────────────────────────────────────────────────────

// Build a { ROLE -> originalQuestionName } map from the marker's fieldQuestionMap so
// values submit under the real admin question names.
function buildFieldMap(marker) {
    const raw = marker?.jsonObj?.fieldQuestionMap || marker?.fieldQuestionMap || [];
    const map = {};
    raw.forEach((m) => {
        // Keep the full entry (name + answerCode). The answerCode is required so the
        // built question submits under the Inform "_0_{answerCode}" binding — without it
        // the backend accepts the POST but does not persist the answer.
        if (m && m.field && m.questionName) {
            map[m.field.toUpperCase()] = { questionName: m.questionName, answerCode: m.answerCode || "" };
        }
    });
    return map;
}

// Resolve the survey question name for an address role. Uses the real admin question
// name when the backend provided one, else a stable prefixed fallback.
function nameForField(marker, fieldMap, role) {
    const key = String(role || "").toUpperCase();
    return fieldMap[key]?.questionName || `${marker.name}__${role}`;
}

// Resolve the Inform answer code for an address role (empty when not provided).
function answerCodeForField(fieldMap, role) {
    const key = String(role || "").toUpperCase();
    return fieldMap[key]?.answerCode || "";
}

// API accessor stashed on the survey model by SurveyForm. When withToken is true the
// authenticated UserToken (also stashed on the model by SurveyForm) is sent as a header —
// needed for party-scoped calls like getpartyaddress. Brand-level lookups
// (getformcountries / getaddressstructure) omit it, matching the original behaviour.
function apiCall(survey, action, identifier, { withToken = false } = {}) {
    const apiGet = survey?.adApiGet;
    if (!apiGet) return Promise.reject(new Error("no adApiGet on survey"));
    const props = {
        controller: survey.adController,
        action,
        environment: survey.adEnvironment,
        identifier
    };
    if (withToken && survey.adUserToken) {
        props.headers = [{ key: "UserToken", value: survey.adUserToken }];
    }
    return apiGet(props);
}

// Maps a latest address (flat object from getpartyaddress) onto the
// address-structure field ROLES the qaddress component builds. The field roles come from
// getaddressstructure (ADDRESS1, ADDRESS2, ADDRESS3, TOWN, COUNTY, POSTCODE, COMPANY…);
// the billing address uses the LocationOption JSON shape (address1, town, postcode…).
function valueForAddressRole(address, role) {
    if (!address) return undefined;
    switch (String(role || "").toUpperCase()) {
        case "ADDRESS1": return address.address1;
        case "ADDRESS2": return address.address2;
        case "ADDRESS3": return address.address3;
        case "TOWN": return address.town;
        case "COUNTY": return address.county;
        case "POSTCODE": return address.postcode;
        case "COMPANY":
        case "COMPANYNAME": return address.companyName;
        // Only used by the standalone (override-mode) pass; the qaddress block builds its own
        // country dropdown and skips COUNTRY when building text fields (FIELDS_TO_SKIP).
        case "COUNTRY": return address.country;
        default: return undefined;
    }
}

// Fetches the authenticated party's latest address (getpartyaddress) at most once per survey
// model. Both the qaddress block and the standalone override-mode pass use it, so a form with
// both makes a single request. Resolves to the address object, or null when there is none or
// the request fails, so callers never need their own catch.
function getPartyAddressOnce(survey) {
    if (!survey) return Promise.resolve(null);
    if (!survey.adPartyAddressPromise) {
        // No identifier: the party is resolved from the UserToken header, and the
        // getpartyaddress route has no {formId} segment. Passing adIdentifier here
        // would build /api/forms/getpartyaddress/{formId} and 404.
        survey.adPartyAddressPromise = apiCall(survey, "getpartyaddress", undefined, { withToken: true })
            .then((resp) => resp?.data?.address || null)
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error("getpartyaddress error:", err);
                return null;
            });
    }
    return survey.adPartyAddressPromise;
}

/**
 * Expand every "addressFieldsFromCountry" marker in the survey into real questions.
 * Idempotent: markers are removed once expanded, so a second call is a no-op.
 */
export function expandAddressFieldsFromCountry(survey) {
    if (!survey) return;

    const markers = survey.getAllQuestions().filter((q) => q.getType() === CUSTOM_TYPE);

    markers.forEach((marker) => {
        // The marker may live directly on a page OR inside a panel/section. Insert the
        // replacement questions into the marker's ACTUAL parent container (and at the
        // marker's index within it) so they land in the correct section — not appended
        // to the end of the page.
        const container = marker.parent || marker.page || survey.currentPage || survey.pages[0];
        if (!container) return;

        const fieldMap = buildFieldMap(marker);
        const countryName = nameForField(marker, fieldMap, "COUNTRY");

        // Opt-in: prefill the authenticated party's latest address, fetching it
        // (getpartyaddress) and mapping it onto the country dropdown + built address fields.
        // Driven by the CONSUMER via the SurveyForm `prefillLatestAddress` prop (stashed on
        // the model as adPrefillLatestAddress) — e.g. CaptureForms opts in, Register does not.
        // A per-marker JSON flag (prefillLatestAddress) is still honoured as an alternative.
        // LatestAddress.js can't be reused here: it renders its own custom address UI, whereas
        // this must bind onto the SurveyJS qaddress component's fields.
        const prefillLatestAddress =
            !!survey.adPrefillLatestAddress
            || !!(marker?.jsonObj?.prefillLatestAddress ?? marker?.prefillLatestAddress);
        // Holds the fetched address until the structure-driven fields exist so
        // buildAddressQuestions can seed each field's value. Cleared once consumed.
        let pendingPrefillAddress = null;

        // Insert the country dropdown at the marker's position within its container.
        const markerIndex = container.elements.indexOf(marker);
        let insertAt = markerIndex >= 0 ? markerIndex : container.elements.length;

       //adding the address component title to the survey json tree
        const componentTitle = marker?.jsonObj?.componentTitle || marker?.componentTitle;
        if (componentTitle) {
            const titleName = `${marker.name}__componentTitle`;
            const titleQ = container.addNewQuestion("html", titleName, insertAt);
            titleQ.html = `<div class="address-component-title">${componentTitle}</div>`;
            insertAt += 1;
        }

        const country = container.addNewQuestion("dropdown", countryName, insertAt);
        country.title = "Country";
        country.titleLocation = "top";
        country.placeholder = "Select a country";
        country.isRequired = true;
        country.choices = [];
        // Country is a real Inform control too — carry its answer code so it persists.
        country.answerCode = answerCodeForField(fieldMap, "COUNTRY");

        // Remember which questions this block owns so we can rebuild cleanly.
        const ownedAddressNames = new Set();

        const clearAddressQuestions = () => {
            ownedAddressNames.forEach((nm) => {
                const q = survey.getQuestionByName(nm);
                if (q) {
                    survey.clearValue(nm); // fresh start on country change
                    (q.parent || container).removeElement(q);
                }
            });
            ownedAddressNames.clear();
        };

        const buildAddressQuestions = (fields) => {
            clearAddressQuestions();
            // Insert right after the country dropdown, in the same container.
            let pos = container.elements.indexOf(country) + 1;
            fields.forEach((field) => {
                const qName = nameForField(marker, fieldMap, field.fieldName);
                const q = container.addNewQuestion("text", qName, pos++);
                q.title = field.label || field.fieldName;
                q.titleLocation = "top";
                q.isRequired = !!field.mandatory;
                if (field.placeHolderText) q.placeholder = field.placeHolderText;
                q.addressFieldRole = field.fieldName;
                // Carry the Inform answer code so prefixAnswers() emits the answercode_
                // companion and saveDemographics can bind the value to the control.
                q.answerCode = answerCodeForField(fieldMap, field.fieldName);
                ownedAddressNames.add(qName);

                // Seed the field from the latest billing address (if one is pending for this
                // build). Only non-empty values are set so required validation still applies
                // to fields the stored address doesn't provide.
                if (pendingPrefillAddress) {
                    const v = valueForAddressRole(pendingPrefillAddress, field.fieldName);
                    if (v !== undefined && v !== null && v !== "") {
                        survey.setValue(qName, v);
                    }
                }
            });
            // The pending address is consumed once per build (the country change that
            // triggered this build). Clear it so a later manual country change starts blank.
            pendingPrefillAddress = null;
        };

        const loadAddressStructure = (countryCode) => {
            if (!countryCode) {
                clearAddressQuestions();
                return;
            }
            apiCall(survey, "getaddressstructure", String(countryCode).toLowerCase())
                .then((response) => {
                    const fields = (response?.data?.addressfields || [])
                        .filter((f) => !FIELDS_TO_SKIP.includes(f.fieldName))
                        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
                    buildAddressQuestions(fields);
                })
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error("getaddressstructure error:", err);
                    clearAddressQuestions();
                });
        };

        // Remove the marker now that the country dropdown stands in its place.
        container.removeElement(marker);

        // The dropdown's VALUE is the country NAME (so it submits/persists the name, matching
        // the standalone country control which is bound to FX_COUNTRY.COUNTRY_NAME). The
        // getaddressstructure lookup still needs the ISO code, so keep a name -> code map.
        const countryNameToCode = {};

        // React to country selection (build/rebuild address fields). Translate the selected
        // name back to its code for the address-structure lookup.
        survey.onValueChanged.add((_sender, options) => {
            if (options.name === countryName) {
                loadAddressStructure(countryNameToCode[options.value]);
            }
        });

        // Populate the country list; restore any existing selection (rebuilds fields).
        apiCall(survey, "getformcountries", survey.adIdentifier)
            .then((response) => {
                const list = (response?.data?.choices || []).filter((c) => c.countrycode && c.name);
                list.forEach((c) => { countryNameToCode[c.name] = c.countrycode; });
                // value = name (persisted answer), text = name (display).
                country.choices = list.map((c) => ({ value: c.name, text: c.name }));

                const existing = survey.getValue(countryName);
                if (existing) {
                    loadAddressStructure(countryNameToCode[existing]);
                    return;
                }

                // No existing selection — if configured, prefill the party's latest billing
                // address. We match the stored country NAME to a country in the list, stash
                // the address so buildAddressQuestions can seed each field, then set the
                // dropdown value: that fires onValueChanged → loadAddressStructure, which
                // builds the fields (consuming pendingPrefillAddress).
                if (!prefillLatestAddress) return;
                getPartyAddressOnce(survey).then((addr) => {
                    if (!addr || !addr.country) return;
                    // The user may have picked a country while the request was in flight.
                    if (survey.getValue(countryName)) return;

                    // Resolve the stored country name to the exact dropdown choice value
                    // (case-insensitive) so SurveyJS accepts the assignment.
                    const matchName = Object.keys(countryNameToCode).find(
                        (n) => n.toLowerCase() === String(addr.country).toLowerCase()
                    );
                    if (!matchName) return;

                    pendingPrefillAddress = addr;
                    // Assigning the value triggers onValueChanged → loadAddressStructure →
                    // buildAddressQuestions, which seeds the text fields from the address.
                    survey.setValue(countryName, matchName);
                });
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error("getformcountries error:", err);
            });
    });
}

/**
 * Pre-fill STANDALONE address questions with the party's latest address.
 *
 * Used when a form overrides the global address field settings: the backend then renders
 * each address field as its own text box / country dropdown (no qaddress marker) and tags
 * each one with "addressFieldRole" (ADDRESS1, TOWN, POSTCODE, COUNTRY…). The role is read
 * from jsonObj, so questions built by the qaddress block (which set addressFieldRole in code,
 * not in the JSON) are never touched here.
 *
 * Gated on the consumer prop (survey.adPrefillLatestAddress). Does nothing — and makes no
 * request — when the form has no tagged questions. Fields that already hold a value (restored
 * autosave, admin default, or anything the user typed while the request was in flight) are
 * left alone, and only non-empty address values are set, so required validation still applies.
 */
export function prefillStandaloneAddressFields(survey) {
    if (!survey || !survey.adPrefillLatestAddress) return;

    const targets = survey.getAllQuestions().filter((q) => q.jsonObj?.addressFieldRole);
    if (targets.length === 0) return;

    getPartyAddressOnce(survey).then((addr) => {
        if (!addr) return;

        targets.forEach((q) => {
            if (!q.isEmpty()) return;

            const role = String(q.jsonObj.addressFieldRole).toUpperCase();
            let value = valueForAddressRole(addr, role);
            if (value === undefined || value === null || value === "") return;

            if (role === "COUNTRY") {
                // The standalone country dropdown loads its choices (value = country name)
                // lazily when it renders. If they're loaded, use the exact matching choice and
                // skip countries the form doesn't offer; if not, set the name and SurveyJS
                // keeps it until the choices arrive.
                const choices = q.choices || [];
                if (choices.length > 0) {
                    const match = choices.find(
                        (c) => String(c.value).toLowerCase() === String(value).toLowerCase()
                    );
                    if (!match) return;
                    value = match.value;
                }
            }

            survey.setValue(q.name, value);
        });
    });
}

export default expandAddressFieldsFromCountry;
